<template>
  <div class="parenting-hub">
    <!-- One front door: Divii mounts this single component. The old
         "Holiday Schedule" menu item can deep-link here via initialTab. -->
    <nav class="hub-tabs" aria-label="Parenting Time sections">
      <button
        v-for="t in HUB_TABS"
        :key="t.value"
        type="button"
        :class="['hub-tab', { active: tab === t.value }]"
        @click="setTab(t.value)"
      >{{ t.label }}</button>
    </nav>

    <!-- Both builders stay mounted (v-show) so in-progress edits and scroll
         positions survive tab switches; hidden panels are display:none, so
         each tab's print button prints only its own deliverable. -->
    <div v-show="tab !== 'holidays'" class="hub-panel">
      <ParentingBuilder
        ref="parenting"
        :partyAName="partyAName"
        :partyBName="partyBName"
        :caseId="caseId"
        :children="children"
        :role="role"
        :currentUserName="currentUserName"
        :holidays="holidaysSnapshot"
        :jurisdiction="jurisdiction"
        :viewMode="tab === 'year' ? 'year' : 'rotation'"
      />
    </div>
    <div v-show="tab === 'holidays'" class="hub-panel">
      <HolidayBuilder
        ref="holiday"
        :partyAName="partyAName"
        :partyBName="partyBName"
        :partyAPronouns="partyAPronouns"
        :partyBPronouns="partyBPronouns"
        :jurisdiction="jurisdiction"
        :caseId="caseId"
        :children="children"
        :role="role"
        :currentUserName="currentUserName"
        :rotation="rotationSnapshot"
      />
    </div>
  </div>
</template>

<script>
import ParentingBuilder from './ParentingBuilder.vue'
import HolidayBuilder from '@/components/ParentingSchedule/holiday/HolidayBuilder.vue'
import { load as loadRotation } from './rotationDraft.js'
import { load as loadHolidays } from '@/components/ParentingSchedule/holiday/holidayDraft.js'

const HUB_TABS = [
  { value: 'rotation', label: 'Weekly Rotation' },
  { value: 'holidays', label: 'Holidays' },
  { value: 'year', label: 'Year Calendar' },
]

export default {
  name: 'ParentingHub',
  components: { ParentingBuilder, HolidayBuilder },
  props: {
    partyAName: { type: String, default: '' },
    partyBName: { type: String, default: '' },
    partyAPronouns: { type: String, default: '' },
    partyBPronouns: { type: String, default: '' },
    caseId: { type: String, default: '' },
    jurisdiction: { type: String, default: 'BC' },
    children: { type: Array, default: () => [] },
    role: { type: String, default: 'primary' },
    currentUserName: { type: String, default: '' },
    // Which tab opens first: 'rotation' | 'holidays' | 'year'. Divii's old
    // "Holiday Schedule" menu item should deep-link with 'holidays'.
    initialTab: { type: String, default: 'rotation' },
  },
  data() {
    return {
      HUB_TABS,
      tab: HUB_TABS.some((t) => t.value === this.initialTab) ? this.initialTab : 'rotation',
      holidaysSnapshot: [],
      rotationSnapshot: null,
    }
  },
  created() {
    this.refreshSnapshots()
  },
  methods: {
    setTab(t) {
      this.tab = t
      // Cross-feed the sibling document on every switch so the Year Calendar
      // sees fresh holiday decisions and the Holidays tab's Calendar pane
      // sees the fresh rotation.
      this.refreshSnapshots()
    },
    // Both stores are Firestore-backed and async, and both builders share
    // this component's caseId.
    //
    // Prefer each builder's live in-memory state over a re-read: saves are
    // debounced by a second, so a fresh Firestore read right after an edit
    // would hand the sibling a stale document. $refs are only empty on the
    // initial call from created(), where the read is the right answer.
    async refreshSnapshots() {
      const parenting = this.$refs.parenting
      const holiday = this.$refs.holiday
      this.rotationSnapshot = parenting?.rotation || (await loadRotation(this.caseId))
      this.holidaysSnapshot = holiday?.holidays?.length
        ? holiday.holidays
        : await loadHolidays(this.caseId, this.partyAName, this.partyBName, this.jurisdiction)
    },
  },
}
</script>

<style scoped>
/* The tab strip stays put and the active panel scrolls beneath it.
   Without this the whole hub scrolled as one block and the tabs — the only way
   back to Weekly Rotation — slid off the top of the viewport. */
.parenting-hub {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
}
/* Only this region is height-capped, so the tab strip above stays visible while
   the active builder scrolls. max-height (not height) means short content hugs
   itself and leaves no dead space — same approach as PropertySchedule's table. */
.hub-panel {
  min-height: 0;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 190px);
  overflow-y: auto;
}
.hub-tabs {
  display: flex;
  flex-shrink: 0;
  gap: 0.4rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.45rem;
}
.hub-tab {
  font-family: inherit;
  font-size: 0.88rem;
  font-weight: 500;
  padding: 0.5rem 1.1rem;
  border-radius: 7px;
  cursor: pointer;
  background: transparent;
  border: none;
  color: #64748b;
}
.hub-tab:hover { color: #34555f; background: #eef3f4; }
.hub-tab.active {
  background: #34555f;
  color: #fff;
}

@media print {
  .hub-tabs { display: none !important; }
}
</style>
